<template>
  <div id="app" class="relative mx-2 border-x custom_border md:mx-6 lg:mx-8 2xl:mx-10">
    <Header />
    <Intro />

    <main class="page-transition min-h-[100vh]">
      <NuxtPage
        :transition="{
          mode: 'out-in',
          css: false,
          onBeforeEnter: beforeEnter,
          onEnter: enter,
          onLeave: leave,
        }"
      />
    </main>

    <Footer />

    <SeasonGreeting />
    <SeasonEffect />
    <SeasonSnow />
  </div>
</template>

<script setup>
import seasonResolverSource from '~/utils/seasonResolver.js?raw'

const transitionStore = usePageTransitionStore()
const { $lenis } = useNuxtApp()
const { locale, locales } = useI18n()
const i18nHead = useLocaleHead({ seo: true })

const organizationSchema = useOrganizationSchema()
const season = useSeason()
const seasonConfig = useAppConfig().season

// The site is statically generated, so the seasonal theme is picked in the browser. This inline
// script runs before first paint, sets data-season from the visitor's clock and keeps the result
// in window.__season for the app, so the themed colours are there from the first frame.
useHead({
  script: [{
    key: 'season',
    innerHTML: `try{${seasonResolverSource.replace('export function', 'function')};var s=resolveSeason(new Date(),${JSON.stringify(seasonConfig)},location.search);window.__season=s;if(s)document.documentElement.setAttribute('data-season',s.name)}catch(e){}`,
  }],
})

// Site-wide Organization + WebSite JSON-LD. Titles, descriptions and the canonical URL come from
// each page's own useSiteSeo() call (the 404 page is noindex and has no canonical).
useHead(() => ({
  script: [{ type: 'application/ld+json', innerHTML: JSON.stringify(organizationSchema()) }],
}))

useHead(() => ({
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs?.lang,
    dir: locales.value.find((l) => l.code === locale.value)?.dir || 'ltr',
    'data-theme': 'light',
    // Seasonal accent palette (main.css); absent outside a season.
    'data-season': season.value?.name,
  },
  // useSiteSeo sets canonical and og:url for every page (none on the noindex 404 page).
  link: (i18nHead.value.link || []).filter((l) => l.rel !== 'canonical'),
  meta: (i18nHead.value.meta || []).filter((m) => m.property !== 'og:url'),
}))

// GSAP is not in the entry bundle: fetch it once the page is idle, before the first navigation needs it.
onMounted(() => {
  // After hydration, so the greeting and effects render on the client only (no mismatch).
  // Reuse the head script's result: by now the router's first navigation may have dropped the
  // ?season= query from the URL.
  try {
    season.value = window.__season !== undefined ? window.__season : resolveSeason(new Date(), seasonConfig, location.search)
  } catch {}
  onIdle(() => document.body.classList.add('noise_ready'))
  onIdle(loadGsap, 4000)
})

const beforeEnter = (el) => {
  el.style.opacity = '0'
  el.style.transform = 'translateY(50px)'
}

const leave = async (el, done) => {
  transitionStore.startTransition()
  const { gsap } = await loadGsap()
  gsap.to(el, {
    opacity: 0,
    y: -50,
    duration: 0.35,
    ease: 'power2.inOut',
    onComplete: done,
  })
}

const enter = async (el, done) => {
  transitionStore.startTransition()
  if ($lenis?.value) $lenis.value.scrollTo(0, { immediate: true })
  const { gsap } = await loadGsap()

  gsap
    .timeline()
    .fromTo(
      el,
      { opacity: 0, y: 50 },
      { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' },
    )
    .eventCallback('onComplete', () => {
      transitionStore.finishTransition()
      done()
    })
}

</script>

<style>
.page-transition {
  position: relative;
  overflow: hidden;
}
</style>
